<template>
  <v-dialog v-if="showCustomerButton"
            v-model="customerPickerDialog"
            width="800px"
            persistent>
    <template v-slot:activator="{ on, attrs }">
      <v-btn icon
             class="mr-2"
             v-bind="attrs"
             v-on="on">
        <v-icon v-if="hasSelectedCustomers">
          fas fa-fw fa-user
        </v-icon>
        <v-icon v-else>
          fal fa-fw fa-user
        </v-icon>
      </v-btn>
    </template>
    <template v-slot:default>
      <v-form @submit.prevent="parameterizedSearch(query)">
        <v-card flat
                tile
                :loading="form.busy">
          <v-card-title>
            Search for your customer in {{ team.crm_name ?? team.crm_provider_name }}
          </v-card-title>
          <v-card-text>
            <v-text-field v-model="query"
                          autofocus
                          label="Search for your customer"
                          outlined
                          clearable
                          @click:clear="clearResults"
                          append-icon="fal fa-fw fa-search"
                          hide-details
                          required
                          :error-messages="form.errors.get('q')">
            </v-text-field>
            <div v-if="(results && results.length) || hasSelectedCustomers"
                 class="d-flex mt-4"
                 style="height: 300px; overflow-y: auto;">
              <div class="me-12">
                <h3 class="text-h6">
                  Search Results
                </h3>
                <v-checkbox v-for="customer in results"
                            :key="customer.id"
                            :input-value="isSelected(customer)"
                            @change="toggleCustomer(customer)"
                            hide-details
                            class="my-1 py-0 ms-2">
                  <template v-slot:label>
                    <div>
                      <div>
                        {{ customer.name }}
                      </div>
                      <div class="text-caption">
                        {{ customer.object }}
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </div>
              <v-spacer></v-spacer>
              <div class="me-2">
                <h3 class="text-h6">
                  Selected Customers
                </h3>
                <div v-for="customer in selectedCustomers"
                     :key="customer.id"
                     class="d-flex align-center my-1">
                  <v-btn @click.prevent="removeCustomer(customer)"
                         icon
                         x-small
                         class="mr-2">
                    <v-icon>
                      fas fa-fw fa-user-minus
                    </v-icon>
                  </v-btn>
                  <div>
                    <div class="text-body-1">
                      {{ customer.name }}
                    </div>
                    <div class="text-caption">
                      {{ customer.object }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="px-4">
            <v-btn type="button"
                   color="primary"
                   :disabled="hasSelectedCustomers === false"
                   @click.prevent="selectCustomersAndClose">
              Select {{ selectedCustomers.length | pluralize('customer') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn type="button"
                   @click.prevent="selectNoCustomerAndClose">
              No customer
            </v-btn>
            <v-btn type="button"
                   @click.prevent="cancelSelection">
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </template>
  </v-dialog>
</template>
<script>
import {Form} from 'vform';
import salesforceService from '../../../../../ml/services/salesforceService';
import {mapGetters} from 'vuex';
import {cloneDeep, isArray, isString} from 'lodash';

export default {
  data() {
    return {
      dialog: false,
      model: false,
      query: '',
      form: new Form({
        q: '',
        fields: [
          'id',
          'name',
        ],
        sobjects: [
          {
            name: "Account",
            fields: [
              'id',
              'name',
            ],
            limit: 20,
          },
          {
            name: "Contact",
            fields: [
              'id',
              'name',
            ],
            limit: 20,
          },
        ],
        in: 'All',
        overallLimit: 40,
        defaultLimit: 20,
      }),
      selectedCustomers: [],
      results: [],
    }
  },
  watch: {
    storedCustomers(newVal, oldVal) { // MLD-6667
      if (newVal !== oldVal) {
        this.cloneStoredCustomers();
      }
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'showCustomerButton',
    ]),
    hasSelectedCustomers() {
      return isArray(this.selectedCustomers)
        && this.selectedCustomers.length > 0;
    },
    storedCustomers: {
      get() {
        return this.$store.state.content.selectedCustomers;
      }
    },
    customerPickerDialog: {
      get() {
        return this.$store.state.content.customerPickerDialog;
      },
      set(customerPickerDialog) {
        this.$store.dispatch('content/setCustomerPickerDialog', customerPickerDialog);
      }
    }
  },
  mounted() {
    this.cloneStoredCustomers();
  },
  methods: {
    cloneStoredCustomers() {
      this.selectedCustomers = cloneDeep(this.storedCustomers) ?? [];
    },
    async parameterizedSearch(query) {
      if (isString(query)) {
        //console.log('parameterizedSearch', query);
        if (query && query.trim()) {
          //console.log('Searching for ', query);
          this.form.q = query;
          const results = await salesforceService.parameterizedSearch(this.form);
          results.forEach(result => {
            result.objectID = JSON.stringify(result);
          })
          this.results = results;
        }
      }
    },
    clearResults() {
      //console.log('clearResults');
      this.results = [];
    },
    isSelected(customer) {
      // @see https://chatgpt.com/c/66edb71a-03ec-8002-9aa9-d8ebd0f1c5d7
      return this.selectedCustomers.some(selected => selected.id === customer.id);
    },
    toggleCustomer(customer) {
      // @see https://chatgpt.com/c/66edb71a-03ec-8002-9aa9-d8ebd0f1c5d7
      // Toggle the selection of a customer
      const index = this.selectedCustomers.findIndex(selected => selected.id === customer.id);
      if (index === -1) {
        // If customer is not selected, add them
        this.selectedCustomers.push(customer);
      } else {
        // If customer is already selected, remove them
        //this.selectedCustomers.splice(index, 1);
        this.selectedCustomers = this.selectedCustomers.filter(c => c.id !== customer.id);
      }
    },
    clearSelectedCustomers() {
      this.selectedCustomers = [];
      const customers = cloneDeep(this.selectedCustomers);
      this.$store.dispatch('content/setSelectedCustomers', customers);
    },
    selectCustomersAndClose() {
      const customers = cloneDeep(this.selectedCustomers);
      this.$store.dispatch('content/setSelectedCustomers', customers);
      this.closeDialog();
    },
    selectNoCustomerAndClose() {
      this.clearSelectedCustomers();
      this.$store.dispatch('content/setBypassCustomerSelection', true);
      this.closeDialog();
    },
    cancelSelection() {
      this.clearSelectedCustomers();
      this.$store.dispatch('content/setBypassCustomerSelection', false);
      this.closeDialog();
    },
    closeDialog() {
      this.$store.dispatch('content/setCustomerPickerDialog', false);
    },
    removeCustomer(customer) {
      this.selectedCustomers = this.selectedCustomers.filter(c => c.id !== customer.id);
    }
  }
}
</script>
